<script>
  /**
   * Pantalla de configuración.
   *
   * Lo único obligatorio es el secreto. La dirección de la API se deduce sola,
   * porque el backend vive en el mismo dominio que la app.
   */
  import { db } from '../lib/db.js';
  import { leer, guardar, estaConfigurada, exportar, descargar, borrarTodo } from '../lib/configuracion.js';
  import { pedirPersistencia } from '../lib/sincronizacion.js';
  import { pendientesDeSubir } from '../lib/repositorio.js';
  import { Api } from '../lib/api.js';

  let { abierta = $bindable(false), sincronizador = null, alAbrirImportar = () => {} } = $props();

  let cfg = $state(leer());
  let probando = $state(false);
  let resultadoPrueba = $state(null);
  let pendientes = $state(0);
  let persistencia = $state(null);
  let exportando = $state(false);

  // Empezar de nuevo
  let modoBorrar = $state(false);
  let palabraBorrar = $state('');
  let borrando = $state(false);
  let resultadoBorrado = $state(null);
  let conteo = $state({ movimientos: 0, cuentas: 0 });

  $effect(() => {
    if (!abierta) return;
    cfg = leer();
    resultadoPrueba = null;
    resultadoBorrado = null;
    modoBorrar = false;
    palabraBorrar = '';
    pendientesDeSubir().then((n) => (pendientes = n));
    pedirPersistencia().then((p) => (persistencia = p));
    // Se cuenta lo que hay para que el aviso diga cuánto se va a perder de
    // verdad, no un "todos tus datos" abstracto.
    Promise.all([db.movimientos.count(), db.cuentas.count()])
      .then(([m, c]) => (conteo = { movimientos: m, cuentas: c }));
  });

  async function ejecutarBorrado() {
    if (palabraBorrar !== 'BORRAR TODO' || borrando) return;
    borrando = true;
    try {
      const r = await borrarTodo(db, sincronizador?.api ?? null);
      if (r.local) {
        resultadoBorrado = {
          ok: true,
          texto: 'Listo, quedó en cero. Recarga la app para empezar de nuevo.',
        };
        // Se recarga sola: la base ya no existe y seguir usando la pantalla
        // con referencias muertas solo produciría errores raros.
        setTimeout(() => location.reload(), 1800);
      } else {
        resultadoBorrado = { ok: false, texto: r.nota ?? 'No se pudo borrar.' };
      }
    } catch (e) {
      resultadoBorrado = { ok: false, texto: String(e.message || e) };
    } finally {
      borrando = false;
    }
  }

  function actualizar(campo, valor) {
    cfg = guardar({ [campo]: valor });
    if (sincronizador) {
      sincronizador.api.url = cfg.url;
      sincronizador.api.secreto = cfg.secreto;
    }
  }

  /**
   * Prueba la conexión antes de guardar nada.
   * Distingue los tres fallos que importan, porque cada uno se arregla
   * distinto: no hay red, la dirección está mal, o el secreto no coincide.
   */
  async function probar() {
    probando = true;
    resultadoPrueba = null;
    try {
      const api = new Api({ url: cfg.url, secreto: cfg.secreto });
      const antes = performance.now();
      await api.sincronizarReloj();
      const ms = Math.round(performance.now() - antes);
      resultadoPrueba = {
        ok: true,
        texto: `Conectado. Respondió en ${ms} ms.`,
        detalle: Math.abs(api.desfaseReloj) > 2000
          ? `Tu reloj está ${Math.round(api.desfaseReloj / 1000)} s desfasado. Se corrigió solo.`
          : null,
      };
    } catch (e) {
      const msg = String(e.message || e);
      resultadoPrueba = {
        ok: false,
        texto: /firma/i.test(msg)
          ? 'El secreto no coincide con el del servidor.'
          : /conexión|sin conexión/i.test(msg)
            ? 'No hay conexión, o la dirección está mal.'
            : msg,
      };
    } finally {
      probando = false;
    }
  }

  async function sincronizarAhora() {
    if (!sincronizador) return;
    probando = true;
    const r = await sincronizador.sincronizar({ motivo: 'manual' });
    pendientes = await pendientesDeSubir();
    resultadoPrueba = r.ok
      ? { ok: true, texto: `Listo. Subieron ${r.subidos} cambios.` }
      : { ok: false, texto: r.error || 'No se pudo sincronizar.' };
    probando = false;
  }

  async function respaldar() {
    exportando = true;
    try {
      const contenido = await exportar(db);
      const fecha = new Date().toISOString().slice(0, 10);
      descargar(contenido, `respaldo-finanzas-${fecha}.json`);
    } finally {
      exportando = false;
    }
  }
</script>

{#if abierta}
  <div class="pantalla">
    <header>
      <button class="volver" onclick={() => (abierta = false)} aria-label="Volver">←</button>
      <h1>Configuración</h1>
    </header>

    <div class="contenido">
      <!-- ── Sincronización ── -->
      <section>
        <h2>Sincronización</h2>
        <p class="explica">
          Tu celular y tu PC comparten los mismos datos. La app guarda primero
          aquí y sube cuando haya señal, así que puedes registrar sin internet.
        </p>

        <label>
          <span class="etiqueta">Dirección del servidor</span>
          <input
            type="url"
            value={cfg.url}
            oninput={(e) => actualizar('url', e.target.value)}
            placeholder="https://…/api"
            autocomplete="off"
            spellcheck="false"
          />
          <span class="ayuda">Se llena sola. Solo cámbiala si sabes lo que haces.</span>
        </label>

        <label>
          <span class="etiqueta">Secreto compartido</span>
          <input
            type="password"
            value={cfg.secreto}
            oninput={(e) => actualizar('secreto', e.target.value)}
            placeholder="Pégalo aquí"
            autocomplete="off"
            spellcheck="false"
          />
          <span class="ayuda">
            Está en el archivo <code>SECRETO-APP.txt</code> del proyecto.
            Es lo que prueba que quien escribe eres tú.
          </span>
        </label>

        <div class="botones">
          <button class="secundario" onclick={probar} disabled={probando || !cfg.url}>
            {probando ? 'Probando…' : 'Probar conexión'}
          </button>
          {#if estaConfigurada(cfg)}
            <button class="secundario" onclick={sincronizarAhora} disabled={probando}>
              Sincronizar ahora
            </button>
          {/if}
        </div>

        {#if resultadoPrueba}
          <p class="resultado" class:mal={!resultadoPrueba.ok}>
            {resultadoPrueba.texto}
            {#if resultadoPrueba.detalle}<br /><small>{resultadoPrueba.detalle}</small>{/if}
          </p>
        {/if}

        <p class="estado-linea">
          {#if pendientes > 0}
            Hay <strong>{pendientes}</strong> cambios esperando subir.
          {:else}
            Todo está sincronizado.
          {/if}
        </p>
      </section>

      <!-- ── Seguridad de los datos ── -->
      <section>
        <h2>Tus datos</h2>
        <p class="explica">
          Todo vive en este dispositivo. El navegador puede borrar sus datos si
          se queda sin espacio, así que conviene bajar un respaldo de vez en
          cuando.
        </p>

        {#if persistencia}
          <p class="estado-linea" class:aviso={!persistencia.concedido}>
            {#if persistencia.concedido}
              El navegador se comprometió a no borrar tus datos.
            {:else}
              El navegador <strong>no</strong> garantizó conservar tus datos.
              Instala la app para que lo haga, y baja un respaldo.
            {/if}
          </p>
        {/if}

        <div class="botones">
          <button class="secundario" onclick={respaldar} disabled={exportando}>
            {exportando ? 'Preparando…' : 'Bajar respaldo'}
          </button>
          <button class="secundario" onclick={() => { abierta = false; alAbrirImportar(); }}>
            Traer de Excel
          </button>
        </div>
      </section>

      <!-- ── Empezar de nuevo ── -->
      <section>
        <h2>Empezar de nuevo</h2>
        <p class="explica">
          Borra todos tus movimientos, cuentas y facturas, aquí y en el
          servidor. La app queda como recién instalada. <strong>No se puede
          deshacer.</strong>
        </p>

        {#if !modoBorrar}
          <div class="botones">
            <button class="peligroso" onclick={() => (modoBorrar = true)}>
              Borrar todo y empezar de cero
            </button>
          </div>
        {:else}
          <div class="zona-peligro">
            <p class="aviso-fuerte">
              Se van a borrar <strong>{conteo.movimientos} movimientos</strong> y
              <strong>{conteo.cuentas} cuentas</strong>.
            </p>
            <p class="explica">
              Antes de seguir, baja un respaldo. Si te arrepientes después, es
              lo único que te va a quedar.
            </p>
            <button class="secundario" onclick={respaldar} disabled={exportando}>
              {exportando ? 'Preparando…' : 'Bajar respaldo primero'}
            </button>

            <label>
              <span class="etiqueta">Escribe <code>BORRAR TODO</code> para confirmar</span>
              <input
                type="text"
                bind:value={palabraBorrar}
                placeholder="BORRAR TODO"
                autocomplete="off"
                spellcheck="false"
              />
            </label>

            <div class="botones">
              <button class="secundario" onclick={() => { modoBorrar = false; palabraBorrar = ''; }}>
                Cancelar
              </button>
              <button
                class="peligroso-firme"
                disabled={palabraBorrar !== 'BORRAR TODO' || borrando}
                onclick={ejecutarBorrado}
              >
                {borrando ? 'Borrando…' : 'Borrar todo'}
              </button>
            </div>
          </div>
        {/if}

        {#if resultadoBorrado}
          <p class="resultado" class:mal={!resultadoBorrado.ok}>{resultadoBorrado.texto}</p>
        {/if}
      </section>

      <!-- ── Lectura de comprobantes ── -->
      <section>
        <h2>Leer comprobantes con IA</h2>
        <p class="explica">
          Tomarle una foto a un comprobante y que la app saque el monto, la
          fecha y el banco. Requiere el plan pago de Gemini, porque en el plan
          gratis Google puede leer y usar lo que envías, y un comprobante
          lleva tu nombre y tu número de cuenta.
        </p>
        <p class="estado-linea">
          Todavía no está activado. La entrada por texto y a mano funciona
          igual, y esa nunca manda nada afuera.
        </p>
      </section>
    </div>
  </div>
{/if}

<style>
  .pantalla {
    position: fixed;
    inset: 0;
    z-index: var(--z-hoja);
    background: var(--bg-canvas);
    overflow-y: auto;
    max-width: var(--w-app);
    margin-inline: auto;
    animation: entrar var(--d-normal) var(--ease-entrada);
  }
  @keyframes entrar {
    from { opacity: 0; transform: translateX(16px) }
    to   { opacity: 1; transform: translateX(0) }
  }

  header {
    position: sticky;
    top: 0;
    z-index: var(--z-pegajoso);
    display: flex;
    align-items: center;
    gap: var(--e-2);
    padding: calc(var(--e-3) + var(--safe-t)) var(--gutter) var(--e-3);
    background: var(--bg-canvas);
    border-bottom: 1px solid var(--border);
  }
  .volver {
    width: var(--tap);
    height: var(--tap);
    display: grid;
    place-items: center;
    font-size: var(--t-xl);
    border-radius: var(--r-full);
    margin-left: calc(var(--e-3) * -1);
  }
  h1 { font-size: var(--t-lg); font-weight: var(--w-semibold) }

  .contenido {
    padding: var(--e-4) var(--gutter) calc(var(--e-16) + var(--safe-b));
    display: flex;
    flex-direction: column;
    gap: var(--e-8);
  }

  section { display: flex; flex-direction: column; gap: var(--e-3) }

  h2 {
    font-size: var(--t-xs);
    font-weight: var(--w-semibold);
    text-transform: uppercase;
    letter-spacing: var(--track-wide);
    color: var(--ink-3);
  }

  .explica {
    font-size: var(--t-sm);
    color: var(--ink-2);
    line-height: var(--lh-loose);
  }

  label { display: flex; flex-direction: column; gap: var(--e-1) }

  .etiqueta {
    font-size: var(--t-sm);
    font-weight: var(--w-medium);
    color: var(--ink-1);
  }

  input {
    padding: var(--e-3);
    /* 16px mínimo: por debajo, iOS hace zoom solo al enfocar. */
    font-size: var(--t-base);
    background: var(--bg-surface);
    border: 1px solid var(--border-strong);
    border-radius: var(--r-md);
    min-height: var(--tap);
  }

  .ayuda {
    font-size: var(--t-xs);
    color: var(--ink-3);
    line-height: var(--lh-normal);
  }
  code {
    font-size: 0.95em;
    background: var(--bg-sunken);
    padding: 1px 4px;
    border-radius: var(--r-xs);
  }

  .botones { display: flex; gap: var(--e-2); flex-wrap: wrap }

  .secundario {
    padding: var(--e-2) var(--e-4);
    border-radius: var(--r-md);
    font-size: var(--t-sm);
    font-weight: var(--w-medium);
    background: var(--bg-sunken);
    border: 1px solid var(--border-strong);
    min-height: var(--tap);
  }
  .secundario:disabled { opacity: 0.45 }

  .resultado {
    font-size: var(--t-sm);
    padding: var(--e-2) var(--e-3);
    border-radius: var(--r-md);
    background: var(--ingreso-suave);
    color: var(--ingreso);
  }
  .resultado.mal {
    background: var(--peligro-suave);
    color: var(--peligro);
  }

  .estado-linea { font-size: var(--t-sm); color: var(--ink-3) }
  .estado-linea.aviso { color: var(--alerta) }

  /* ── Zona de peligro ──
     Va al final de la pantalla y con su propio marco. Que cueste llegar es
     parte del diseño: nadie borra años de datos por un toque sin querer. */
  .peligroso {
    padding: var(--e-3) var(--e-4);
    border-radius: var(--r-md);
    color: var(--peligro);
    background: var(--peligro-suave);
    font-weight: var(--w-medium);
    min-height: var(--tap);
  }
  .peligroso-firme {
    flex: 1;
    padding: var(--e-3);
    border-radius: var(--r-md);
    background: var(--peligro);
    color: var(--on-peligro);
    font-weight: var(--w-semibold);
    min-height: var(--tap);
  }
  .peligroso-firme:disabled { opacity: 0.35 }

  .zona-peligro {
    display: flex;
    flex-direction: column;
    gap: var(--e-3);
    padding: var(--e-3);
    border: 1px solid var(--peligro);
    border-radius: var(--r-md);
    background: var(--peligro-suave);
  }
  .aviso-fuerte { font-size: var(--t-sm); color: var(--peligro) }
  .zona-peligro .explica { color: var(--ink-2) }
</style>
